import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { eventAseUpdateAuthor, NAVIGATE_MODAL_BACK } from 'store/actions';

type DraftChanges = {
  id: string;
  name: string;
  email: string;
  role: string;
};

export const AuthorScreen: React.FC<
  ModalScreenProps<ModalScreens.AseAuthor>
> = ({ route, navigation }) => {
  const { eventKey, data } = route.params;
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const dataRef = React.useRef<DraftChanges>({ ...data });
  const initDataRef = React.useRef<DraftChanges>(cloneDeep(dataRef.current));

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  isDirtyRef.current = checkIsDirty();

  const handleHardSave = React.useCallback(() => {
    if (checkIsDirty()) {
      dispatch(eventAseUpdateAuthor({ key: eventKey, data: dataRef.current }));
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [checkIsDirty, dispatch, eventKey]);

  const handleClose = React.useCallback(() => {
    if (checkIsDirty()) {
      confirmUnsaved({
        onSave: handleHardSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [checkIsDirty, confirmUnsaved, dispatch, handleHardSave]);

  const handleSoftSave = React.useCallback(
    (
      changes: Partial<DraftChanges>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        dataRef.current = newValues;
        if (
          doForceUpdate === 'always' ||
          (doForceUpdate === 'dirtyChange' &&
            checkIsDirty() !== isDirtyRef.current)
        ) {
          forceUpdate();
        }
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const saveField = React.useCallback(
    (name: keyof DraftChanges, value: string) =>
      handleSoftSave({ [name]: value }, { doForceUpdate: 'dirtyChange' }),
    [handleSoftSave],
  );

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Author"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleHardSave}
        />
      }
    >
      <Card label="Name & Email">
        <CardRow value={dataRef.current.name} />
        <CardRow value={dataRef.current.email} />
      </Card>
      <Card label="Role">
        <TextField
          name="role"
          onChangeWithName={saveField}
          value={dataRef.current.role}
          placeholder="Assessor"
          onEsc={handleClose}
        />
      </Card>
    </Page>
  );
};
